import React from 'react';
import { Filter, Layers, Maximize, Minimize } from 'lucide-react';
import PremiumDropdown from '../../PremiumDropdown';
import { SubHeaderCalendarNavControls } from './SubHeaderCalendarNavControls';
import { SubHeaderPresence } from '../SubHeaderPresence';
import { SubHeaderExportDropdown } from '../dropdowns/SubHeaderExportDropdown';
import { SubHeaderToolsDropdown } from '../dropdowns/SubHeaderToolsDropdown';
import { FullProject } from '../../../types';

export interface SubHeaderRightActionsProps {
  viewMode: string;
  isProjectView: boolean;
  isGlobalView: boolean;
  activeProject: FullProject | null;
  activeMetricsTab?: 'overview' | 'roadmap' | 'time' | 'projects' | 'library' | 'ideaPool' | 'annualReport' | 'activity';
  selectedMetricsProjectId: string;
  setSelectedMetricsProjectId: (id: string) => void;
  dropdownProjects: FullProject[];
  onZoomToFit?: () => void;
  isZoomedToFit?: boolean;
  showFilters: boolean;
  setShowFilters: (val: boolean) => void;
  isFilterActive: boolean;
  calendarDate: Date;
  setCalendarDate: (date: Date) => void;
  calendarViewType: 'month' | 'week';
  setCalendarViewType: (type: 'month' | 'week') => void;
  calendarFilterMode?: 'all' | 'tasks' | 'milestones' | 'deadlines';
  setCalendarFilterMode?: (mode: 'all' | 'tasks' | 'milestones' | 'deadlines') => void;
  showCompleted?: boolean;
  setShowCompleted?: (val: boolean) => void;
  showArchived?: boolean;
  setShowArchived?: (val: boolean) => void;
  showTaskDetails?: boolean;
  setShowTaskDetails?: (val: boolean) => void;
  communicationTab: 'board' | 'database' | 'network' | 'archive';
  onOpenExportPro?: () => void;
  exportAsExcel: () => void;
  exportTaskTableAsExcel: () => void;
  handleDownloadRoadmap: () => void;
  showToolsDropdown: boolean;
  setShowToolsDropdown: (show: boolean) => void;
  setShowDataDropdown: (show: boolean) => void;
  toolsDropdownRef: React.RefObject<HTMLDivElement | null>;
  setViewMode: (mode: any) => void;
  onPrefetch?: (view: string) => void;
  onShowBiography?: () => void;
  onShowTimeMachine?: () => void;
}

export const SubHeaderRightActions: React.FC<SubHeaderRightActionsProps> = ({
  viewMode,
  isProjectView,
  isGlobalView,
  activeProject,
  activeMetricsTab,
  selectedMetricsProjectId,
  setSelectedMetricsProjectId,
  dropdownProjects,
  onZoomToFit,
  isZoomedToFit = false,
  showFilters,
  setShowFilters,
  isFilterActive,
  calendarDate,
  setCalendarDate,
  calendarViewType,
  setCalendarViewType,
  calendarFilterMode = 'all',
  setCalendarFilterMode,
  showCompleted = true,
  setShowCompleted,
  showArchived = false,
  setShowArchived,
  showTaskDetails = true,
  setShowTaskDetails = () => {},
  communicationTab,
  onOpenExportPro,
  exportAsExcel,
  exportTaskTableAsExcel,
  handleDownloadRoadmap,
  showToolsDropdown,
  setShowToolsDropdown,
  setShowDataDropdown,
  toolsDropdownRef,
  setViewMode,
  onPrefetch,
  onShowBiography,
  onShowTimeMachine
}) => {
  return (
    <div className="flex-1 flex items-center justify-end gap-2">
      {/* Metrics Project Selector */}
      {viewMode === 'metricsDashboard' && activeMetricsTab !== 'projects' && activeMetricsTab !== 'library' && activeMetricsTab !== 'ideaPool' && activeMetricsTab !== 'annualReport' && (
        <div className="flex items-center gap-2 h-10 mr-2">
          <PremiumDropdown
            value={selectedMetricsProjectId}
            onChange={(val) => {
              setSelectedMetricsProjectId(val || 'all');
            }}
            options={[
              { value: 'all', label: 'Alle aktiven Projekte', icon: <Layers size={12} /> },
              ...dropdownProjects.map(p => ({ value: p.id, label: p.name, color: p.color }))
            ]}
            icon={<Layers size={13} />}
            placeholder="Projekt wählen"
            showSearch={dropdownProjects.length > 5}
            showLabel={true}
            alignRight={true}
            className="h-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm rounded-xl min-w-[200px]"
          />
          <div className="h-4 w-px bg-slate-200 dark:bg-slate-700 mx-1" />
        </div>
      )}

      {/* Project View / Global Gantt Filter and Zoom buttons */}
      {((isProjectView && !isGlobalView) || viewMode === 'globalGantt') && (
        <>
          {viewMode === 'gantt' && onZoomToFit && (
            <button 
              type="button"
              onClick={onZoomToFit}
              aria-pressed={isZoomedToFit}
              aria-label={isZoomedToFit ? "Normalansicht wiederherstellen" : "Gesamtes Gantt darstellen"}
              className={`p-2.5 rounded-xl transition-all flex items-center justify-center border shadow-sm active:scale-95 w-10 h-10 ${
                isZoomedToFit
                  ? 'bg-indigo-50 border-indigo-300 text-indigo-600 dark:bg-indigo-950/40 dark:border-indigo-700 dark:text-indigo-400 font-bold'
                  : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500 hover:text-indigo-600 hover:border-indigo-200 dark:hover:border-indigo-800'
              }`}
              title={isZoomedToFit ? "Normalansicht wiederherstellen" : "Gesamtes Gantt darstellen"}
            >
              {isZoomedToFit ? (
                <Minimize size={18} strokeWidth={2.5} />
              ) : (
                <Maximize size={18} strokeWidth={2.5} />
              )}
            </button>
          )}
          
          <button 
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            aria-pressed={showFilters}
            aria-label="Filter anzeigen oder ausblenden"
            className={`p-2.5 rounded-xl transition-all flex items-center justify-center border shadow-sm active:scale-95 w-10 h-10 relative ${
              showFilters 
                ? 'bg-indigo-600 border-indigo-500 text-white shadow-indigo-200' 
                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500 hover:text-indigo-600 hover:border-indigo-200 dark:hover:border-indigo-800'
            }`}
            title="Filter anzeigen/ausblenden"
          >
            <Filter size={18} strokeWidth={showFilters ? 3 : 2.5} />
            {isFilterActive && (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-400 border-2 border-white dark:border-slate-800 animate-pulse" />
            )}
          </button>
        </>
      )}

      {/* Global Calendar Navigation & Filters */}
      {(viewMode === 'dashboard' || viewMode === 'globalCalendar') && (
        <SubHeaderCalendarNavControls
          calendarDate={calendarDate}
          setCalendarDate={setCalendarDate}
          calendarViewType={calendarViewType}
          setCalendarViewType={setCalendarViewType}
          calendarFilterMode={calendarFilterMode}
          setCalendarFilterMode={setCalendarFilterMode}
          showCompleted={showCompleted}
          setShowCompleted={setShowCompleted}
          showArchived={showArchived}
          setShowArchived={setShowArchived}
          showTaskDetails={showTaskDetails}
          setShowTaskDetails={setShowTaskDetails}
        />
      )}

      {/* Project Specific Actions */}
      {(!isGlobalView && isProjectView) && (
        <div className="flex items-center gap-2">
          {/* Live Presence Avatars */}
          <SubHeaderPresence projectId={activeProject?.id} />

          {/* Export Dropdown */}
          <SubHeaderExportDropdown
            isGlobalView={isGlobalView}
            viewMode={viewMode}
            communicationTab={communicationTab}
            onOpenExportPro={onOpenExportPro}
            exportAsExcel={exportAsExcel}
            exportTaskTableAsExcel={exportTaskTableAsExcel}
            handleDownloadRoadmap={handleDownloadRoadmap}
            onCloseOthers={() => setShowToolsDropdown(false)}
          />

          {/* Tools Dropdown */}
          <SubHeaderToolsDropdown
            showToolsDropdown={showToolsDropdown}
            setShowToolsDropdown={setShowToolsDropdown}
            toolsDropdownRef={toolsDropdownRef}
            setViewMode={setViewMode}
            onPrefetch={onPrefetch}
            onShowBiography={onShowBiography}
            onShowTimeMachine={onShowTimeMachine}
            onCloseOthers={() => setShowDataDropdown(false)}
          />
        </div>
      )}
    </div>
  );
};
